// src/components/PageHeader.jsx
//
// Shared page header used at the top of every content page.
// Structure: icon · title stack (title + subtitle + optional breadcrumb)
// and a right-aligned actions slot. Consistent spacing, responsive font
// sizes, and a soft fade-in so transitions between pages feel fluid.
//
// Usage:
//   <PageHeader
//     icon={<AutomationIcon />}
//     title="Automations"
//     subtitle="Flows triggered by events, calls, or schedules."
//     actions={<Button>New</Button>}
//   />
//
// For split-panel pages (Location/Device/Persons management etc.) where
// the header needs a tight toolbar row directly underneath the title,
// pass `split` + an optional `toolbar` slot. That variant drops the
// bottom margin, uses dense spacing, and stacks toolbar + divider below
// the title row so the detail panel layout underneath can take over.

import React from 'react';
import { Box, Divider, Stack, Typography, useTheme, useMediaQuery } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';

// IMPORTANT: must be defined at module scope, not inside the component.
// `motion(Box)` returns a new component type on each call — recreating it
// inside the render function makes React treat every render as a brand-new
// component, unmounting and remounting the entire subtree (including any
// inputs in the toolbar slot). That was the cause of focus loss in the
// LocationManagement search field while typing.
const MotionBox = motion(Box);

const PageHeader = ({
  icon,
  title,
  subtitle,
  actions,
  dense = false,
  split = false,
  toolbar,
  sx,
}) => {
  const theme = useTheme();
  const isDown = useMediaQuery(theme.breakpoints.down('sm'));
  const prefersReducedMotion = useReducedMotion();

  const titleRow = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 1.5, sm: 2 },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: split ? { xs: 32, md: 36 } : { xs: 36, md: 40 },
              height: split ? { xs: 32, md: 36 } : { xs: 36, md: 40 },
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              flexShrink: 0,
              '& svg': { fontSize: split ? { xs: 18, md: 20 } : { xs: 20, md: 24 } },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant={split || isDown ? 'h6' : 'h5'}
            noWrap
            // The title comes from the h5/h6 variant (display font under Caro Editorial). The
            // bold weight is today's look; a theme may override it via theme.caro.legacy
            // (Editorial keeps Newsreader at its 500 heading weight).
            sx={(theme) => ({
              fontWeight: 700,
              lineHeight: 1.2,
              ...(theme.caro?.legacy?.pageHeaderTitle || {}),
            })}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: split ? 1 : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                maxWidth: 720,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
      {actions && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {actions}
        </Box>
      )}
    </Box>
  );

  return (
    <MotionBox
      initial={prefersReducedMotion ? false : { opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      sx={{
        mb: split ? 1 : (dense ? 1.5 : { xs: 2, md: 3 }),
        ...sx,
      }}
    >
      {titleRow}
      {split && toolbar && (
        <Box sx={{ mt: 1 }}>
          {toolbar}
        </Box>
      )}
      {split && <Divider sx={{ mt: toolbar ? 1 : 1.25 }} />}
    </MotionBox>
  );
};

export default PageHeader;
